import type { JSX } from '@gov-design-system-ce/components';
import { useAxiosInstance } from '@gov-nx/api/axios';
import { usePoMutation } from '@gov-nx/api/hooks/usePoMutation';
import { saveUserSettingQuery } from '@gov-nx/api/queries/settingsQuery';
import type { FavoriteCode } from '@gov-nx/api/types/userSettings';
import { CommonSettingKey } from '@gov-nx/api/types/userSettings';
import { useMessageEvents } from '@gov-nx/core/events/Message/useMessageEvents';
import { LocalizeNameSpaceTypes } from '@gov-nx/core/service/translations/name-space.types';
import type { UncheckedKey } from '@gov-nx/core/service/translations/translation.types';
import { useLocale } from '@gov-nx/core/service/translations/useLocale';
import { PageCode } from '@gov-nx/module/page/definitions/codes';
import { isPageCode } from '@gov-nx/module/page/types/pageTypeHelpers';
import { useUserStore, useUserStoreFavorites } from '@gov-nx/store/user/store';

const favoriteCodeToI18nKeypart = (code: FavoriteCode) =>
	isPageCode(code) ? 'stranka' : 'sluzba';

const blacklistedCodes: FavoriteCode[] = [
	PageCode['podpora'],
	PageCode['uzivatelska-prirucka'],
];

export interface FavoriteToggleCommonProps {
	code: FavoriteCode;
	name?: string;
	size?: JSX.GovButton['size'];
	withLabel?: boolean;
	disabled?: boolean;
	customClass?: string;
}

export const useFavoriteToggle = ({
	code,
	name,
}: FavoriteToggleCommonProps) => {
	const setUserSettingFavorites = useUserStore('setUserSettingFavorites');
	const favorites = useUserStoreFavorites();
	const isFavorite = favorites.includes(code);
	const { axiosInstance } = useAxiosInstance();

	const { t } = useLocale(LocalizeNameSpaceTypes.Global);
	const { toastMessageSuccess } = useMessageEvents();

	const i18nStaticParts = ['uzivatel', 'oblibene'];
	const i18nLabelVariableParts = [
		favoriteCodeToI18nKeypart(code),
		isFavorite ? 'odebrat' : 'pridat',
	];

	const label = t<UncheckedKey>(
		[...i18nStaticParts, ...i18nLabelVariableParts].join('.')
	);
	const ariaLabel = t<UncheckedKey>(
		[...i18nStaticParts, 'wcag', ...i18nLabelVariableParts].join('.'),
		{ name }
	);

	const toggleFavorites = (): string[] =>
		isFavorite
			? favorites.filter((item) => item !== code)
			: [...favorites, code];

	const mutation = usePoMutation({
		mutationFn: async (): Promise<boolean> => {
			const toggled = toggleFavorites();
			setUserSettingFavorites(toggled);
			await saveUserSettingQuery(axiosInstance, {
				key: CommonSettingKey.Favorites,
				value: toggled,
			});
			return toggled.includes(code);
		},
		onSuccess: async (updatedIsFavorite) => {
			const i18nToastParts = [
				...i18nStaticParts,
				favoriteCodeToI18nKeypart(code),
				updatedIsFavorite ? 'pridano' : 'odebrano',
			];
			toastMessageSuccess(t<UncheckedKey>(i18nToastParts.join('.')));
		},
	});

	return {
		isEnabled: !blacklistedCodes.includes(code),
		label,
		ariaLabel,
		isFavorite,
		toggle: mutation.mutate,
		isSaving: mutation.isLoading,
	};
};
